import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ban,
  Check,
  Copy,
  ExternalLink,
  FileText,
  FolderPlus,
  GitBranch,
  Mail,
  MessageCircle,
  PenLine,
  ShieldCheck,
} from "lucide-react";

import { api, ApiError } from "@/lib/api-client";
import { missingToSend, type OrderFull } from "@/lib/orders";
import { BTN_GHOST, BTN_GOLD, BTN_GREEN, BTN_PRIMARY } from "./ui";

/** Everything that happens to a saved order: preview, send, sign on site, approve, cancel, revise, convert. */
export function ActionsCard({
  order,
  isAdmin,
  dirty,
  onChanged,
}: {
  order: OrderFull;
  isAdmin: boolean;
  dirty: boolean;
  onChanged: (o?: OrderFull) => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "ok" | "warn" | "err"; text: string; share?: string } | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const run = async (name: string, fn: () => Promise<void>) => {
    setBusy(name);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      const msg = e instanceof ApiError ? (safeMessage(e.message) ?? `Error ${e.status}`) : "Something went wrong";
      setNotice({ tone: "err", text: msg });
      await onChanged();
    } finally {
      setBusy(null);
    }
  };

  const waShare = (text: string) => `https://wa.me/${order.mobile}?text=${encodeURIComponent(text)}`;

  const send = (via: "whatsapp" | "email") =>
    run(via, async () => {
      const res = await api.sendOrder(order.id, { via });
      await onChanged(res);
      if (res.sendResult === "sent") {
        setNotice({ tone: "ok", text: via === "whatsapp" ? "Sent on WhatsApp." : signed ? "Signed copy emailed." : "Email sent." });
      } else if (res.sendResult === "off") {
        setNotice({ tone: "warn", text: `${via === "whatsapp" ? "WhatsApp" : "Email"} sending is not configured on this server.`, share: res.shareText });
      } else {
        setNotice({ tone: "warn", text: `Not delivered (${res.sendResult}). Share it from your phone instead:`, share: res.shareText });
      }
    });

  const missing = order.status === "signed" || order.status === "sent" ? [] : missingToSend(order);
  const incomplete = missing.length > 0;
  const cannotSend = dirty || incomplete || !["draft", "sent", "pending_approval", "signed"].includes(order.status);
  const signed = order.status === "signed";

  return (
    <section className="glass-panel mt-4 p-5">
      <div className="brand-kicker">Actions</div>
      {dirty && <p className="mt-2 text-xs text-goldbright">Save your changes first.</p>}
      {!dirty && incomplete && <p className="mt-2 text-xs text-ice/70">Draft saved. To send or sign, add: <b className="text-paper">{missing.join(", ")}</b>.</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={BTN_GHOST}
          disabled={busy !== null || dirty}
          onClick={() =>
            run("preview", async () => {
              if (order.status === "draft" || order.status === "pending_approval") await api.renderOrder(order.id);
              setPreview(`${api.orderPdfUrl(order.id)}?t=${Date.now()}`);
            })
          }
        >
          <FileText className="h-3.5 w-3.5" /> Preview PDF
        </button>

        <button type="button" className={BTN_GHOST} disabled={busy !== null} title="New draft with the same customer and rooms" onClick={() => run("duplicate", async () => navigate(`/orders/${(await api.duplicateOrder(order.id)).id}`))}>
          <Copy className="h-3.5 w-3.5" /> Duplicate
        </button>

        {order.status !== "signed" && order.status !== "cancelled" && (
          <button type="button" className={BTN_GOLD} disabled={busy !== null || dirty || incomplete} onClick={() => navigate(`/orders/${order.id}/review`)}>
            <PenLine className="h-3.5 w-3.5" /> Review & sign on site
          </button>
        )}

        <button type="button" className={BTN_GREEN} disabled={busy !== null || cannotSend} onClick={() => send("whatsapp")}>
          <MessageCircle className="h-3.5 w-3.5" /> {busy === "whatsapp" ? "Sending…" : signed ? "Signed PDF on WhatsApp" : "Send on WhatsApp"}
        </button>
        <button type="button" className={BTN_PRIMARY} disabled={busy !== null || cannotSend || !order.email} onClick={() => send("email")} title={order.email ? "" : "No email on the order"}>
          <Mail className="h-3.5 w-3.5" /> {busy === "email" ? "Sending…" : signed ? "Signed PDF by email" : "Send by email"}
        </button>

        {order.status === "sent" && (
          <>
            <button
              type="button"
              className={BTN_GHOST}
              onClick={() => {
                void navigator.clipboard.writeText(order.publicLink);
                setNotice({ tone: "ok", text: "Signing link copied." });
              }}
            >
              <Copy className="h-3.5 w-3.5" /> Copy signing link
            </button>
            <a className={BTN_GHOST} href={waShare(`Hello ${order.customerName.split(" ")[0]}, your Versus Concept order ${order.number} is ready to sign: ${order.publicLink}`)} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5" /> Share from my phone
            </a>
          </>
        )}

        {isAdmin && order.status === "pending_approval" && (
          <button type="button" className={BTN_GOLD} disabled={busy !== null} onClick={() => run("approve", async () => onChanged(await api.approveOrder(order.id)))}>
            <ShieldCheck className="h-3.5 w-3.5" /> Approve discount
          </button>
        )}

        {(order.status === "sent" || order.status === "signed") && (
          <button type="button" className={BTN_GHOST} disabled={busy !== null} onClick={() => run("revise", async () => {
            const next = await api.reviseOrder(order.id);
            navigate(`/orders/${next.id}`);
          })}>
            <GitBranch className="h-3.5 w-3.5" /> New revision
          </button>
        )}

        {(order.status === "signed" || order.status === "sent") && !order.projectId && (
          <button type="button" className={BTN_GOLD} disabled={busy !== null} onClick={() => run("convert", async () => {
            const { projectId } = await api.convertOrder(order.id);
            navigate(`/projects/${projectId}`);
          })}>
            <FolderPlus className="h-3.5 w-3.5" /> Create project
          </button>
        )}
        {order.projectId && (
          <button type="button" className={BTN_GHOST} onClick={() => navigate(`/projects/${order.projectId}`)}>
            <Check className="h-3.5 w-3.5" /> Open project
          </button>
        )}

        {order.status !== "signed" && order.status !== "cancelled" && (
          <button
            type="button"
            className={`${BTN_GHOST} text-pink`}
            disabled={busy !== null}
            onClick={() => {
              const reason = window.prompt("Reason for cancelling this order?");
              if (!reason) return;
              void run("cancel", async () => onChanged(await api.cancelOrder(order.id, reason)));
            }}
          >
            <Ban className="h-3.5 w-3.5" /> Cancel
          </button>
        )}
      </div>

      {notice && (
        <div
          className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
            notice.tone === "ok" ? "border-green/50 bg-green/10 text-green" : notice.tone === "warn" ? "border-gold/50 bg-gold/10 text-goldbright" : "border-rose/60 bg-rose/15 text-pink"
          }`}
        >
          {notice.text}
          {notice.share && (
            <a className={`${BTN_GREEN} ml-3 py-1`} href={waShare(notice.share)} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="h-3.5 w-3.5" /> Share on WhatsApp
            </a>
          )}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex flex-col bg-night/90 p-3 backdrop-blur-sm sm:p-6" onClick={() => setPreview(null)}>
          <div className="mb-2 flex items-center justify-between text-xs text-ice/70">
            <span>{order.number} · PDF preview</span>
            <a href={preview} target="_blank" rel="noopener noreferrer" className="text-pink underline" onClick={(e) => e.stopPropagation()}>Open in a new tab</a>
          </div>
          <iframe title="Order PDF" src={preview} className="flex-1 rounded-lg bg-white" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </section>
  );
}

function safeMessage(raw: string): string | null {
  try {
    const parsed = JSON.parse(raw) as { message?: string | string[] };
    const m = parsed.message;
    return Array.isArray(m) ? m.join(", ") : (m ?? null);
  } catch {
    return raw || null;
  }
}
